import React from "react";
import i18next from "i18next";
import { connect } from "react-redux";
import { Dropdown } from "react-bootstrap";
import {
	FiPlus,
	FiMoreHorizontal,
	FiInbox,
	FiFileText,
	FiExternalLink,
	FiUser,
	FiBriefcase,
	FiCalendar,
	FiStar,
	FiHeart,
	FiBookmark,
	FiHome,
	FiSettings,
	FiArchive,
	FiTag,
	FiFolder,
	FiFile,
	FiImage,
	FiVideo,
	FiMusic,
	FiCode,
} from "react-icons/fi";
import { showModal, hideModal } from "actions/modalActions";
import { documentsService, authService } from "services";
import { toastr } from "react-redux-toastr";
import Loading from "components/Loading";
import DataTable from "components/DataTable";
import CopyStoreFoldersModal from "./CopyStoreFoldersModal";
import EmployeeDocumentsUploadModal from "./EmployeeDocumentsUploadModal";
import DocumentMoveModal from "../Documents/DocumentMoveModal";
import DocumentRenameModal from "../Documents/DocumentRenameModal";
import DocumentSignContent from "../Documents/DocumentSignContent";
import SignatureStatus from "../Documents/SignatureStatus";
import {
	isSignableDocument,
	getFormatErrorMessage,
	isHaddockEnvironment,
} from "utils/documentUtils";
import { hasSubscriptionFeature } from "utils/utils";
import UpgradeModal from "components/Modal/UpgradeModal";

const iconMap = {
	"payslips-icon": FiInbox,
	"contracts-icon": FiFileText,
	"absences-icon": FiExternalLink,
	"personal-documents-icon": FiUser,
	"briefcase-icon": FiBriefcase,
	"calendar-icon": FiCalendar,
	"star-icon": FiStar,
	"heart-icon": FiHeart,
	"bookmark-icon": FiBookmark,
	"home-icon": FiHome,
	"settings-icon": FiSettings,
	"archive-icon": FiArchive,
	"tag-icon": FiTag,
	"folder-icon": FiFolder,
	folder: FiFolder,
	document: FiFileText,
	file: FiFile,
	image: FiImage,
	video: FiVideo,
	music: FiMusic,
	code: FiCode,
};

// Botón de 3 puntos personalizado para usar como Toggle del Dropdown
const CustomEllipsisButton = React.forwardRef(({ onClick }, ref) => (
	<button
		ref={ref}
		type="button"
		title={i18next.t("employeeDocuments.moreOptions")}
		className="btn btn-outline-primary"
		onClick={(e) => {
			e.preventDefault();
			onClick(e);
		}}
		style={{
			display: "flex",
			alignItems: "center",
			gap: "5px",
			fontSize: "14px",
			padding: "6px 12px",
			// Integración visual en btn-group
			marginLeft: "-1px", // evita doble borde con el botón anterior
			borderTopLeftRadius: 0,
			borderBottomLeftRadius: 0,
			// Solo bordes redondeados a la derecha
			borderTopRightRadius: "8px",
			borderBottomRightRadius: "8px",
			height: "38px",
		}}
	>
		<FiMoreHorizontal />
	</button>
));

class EmployeeDocuments extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			folders: [],
			attachments: [],
			signaturitData: [],
			currentFolder: null,
			loading: true,
			error: null,
			selectedFolderId: null,
			sortField: "name",
			sortDirection: "asc",
			dataLoaded: false, // Control para saber si ya se cargaron los datos
			showUpgradeModal: false,
			upgradeFeature: "",
		};

		this.closeModal = this.closeModal.bind(this);
		this.openUploadModal = this.openUploadModal.bind(this);
	}

	componentDidMount() {
		// Solo cargar si está visible o si no hay prop isVisible (compatibilidad)
		if (this.props.isVisible !== false) {
			this.fetchFolders();
		}
	}

	componentDidUpdate(prevProps) {
		// Si cambia la visibilidad y ahora es visible, cargar los datos
		if (
			this.props.isVisible &&
			!prevProps.isVisible &&
			!this.state.dataLoaded
		) {
			this.fetchFolders();
		}

		// Si cambia el refreshKey, recargar los datos
		if (
			this.props.refreshKey &&
			this.props.refreshKey !== prevProps.refreshKey
		) {
			this.setState({ dataLoaded: false }, () => {
				this.fetchFolders();
			});
		}

		// Si cambia el empleado, recargar los datos
		if (
			this.props.employee &&
			prevProps.employee &&
			this.props.employee.id !== prevProps.employee.id
		) {
			this.setState({ dataLoaded: false, selectedFolderId: null }, () => {
				this.fetchFolders();
			});
		}
	}

	fetchFolders = () => {
		this.setState({ loading: true });
		documentsService
			.getStoreFolders()
			.then((response) => {
				const folders = response.data || response || [];

				// Manejar caso de lista vacía
				if (!folders || folders.length === 0) {
					this.setState({
						folders: [],
						loading: false,
						error: i18next.t("employeeDocuments.noFoldersAvailable"),
						dataLoaded: true,
					});
					return;
				}

				this.setState({
					folders: folders,
					loading: false,
					error: null,
					dataLoaded: true,
				});

				// Seleccionar carpeta por defecto: priorizar "Nóminas", sino el primero
				if (folders.length > 0 && !this.state.selectedFolderId) {
					const nominasFolder = folders.find(
						(folder) => folder.name.toLowerCase() === "nóminas"
					);

					const defaultFolderId = nominasFolder
						? nominasFolder.id
						: folders[0].id;
					this.selectFolder(defaultFolderId);
				}
			})
			.catch((error) => {
				console.error("❌ Error fetching folders:", error);

				// Manejar diferentes tipos de errores
				let errorMessage = i18next.t("employeeDocuments.errorLoadingFolders");

				if (error.status === 404) {
					errorMessage = i18next.t("employeeDocuments.foldersNotFound");
				} else if (error.message) {
					errorMessage = error.message;
				}

				this.setState({
					error: errorMessage,
					loading: false,
					folders: [],
					dataLoaded: true,
				});
			});
	};

	selectFolder = (folderId) => {
		this.setState({ selectedFolderId: folderId, loading: true });
		this.fetchAttachments(folderId);
	};

	fetchAttachments = (folderId) => {
		const { employee } = this.props;

		// Validar que tenemos un empleado válido antes de hacer la llamada
		if (!employee || !employee.id) {
			console.warn("⚠️ Intentando cargar attachments sin un empleado válido");
			this.setState({
				attachments: [],
				signaturitData: [],
				loading: false,
				error: null,
			});
			return;
		}

		// Hacer ambas llamadas en paralelo
		Promise.all([
			documentsService.getAttachments(folderId, employee.id),
			documentsService.getSignaturitAttachments(folderId),
		])
			.then(([attachmentsResponse, signaturitResponse]) => {
				// Manejar respuesta exitosa incluso si no tiene la propiedad 'ok'
				// Ya que la API puede devolver directamente un array
				const attachments =
					attachmentsResponse.data || attachmentsResponse || [];
				const signaturitData =
					signaturitResponse.data || signaturitResponse || [];
				const currentFolder = this.state.folders.find((f) => f.id === folderId);

				// Ordenar los attachments en el frontend
				const sortedAttachments = this.sortAttachments(attachments);

				this.setState({
					attachments: sortedAttachments,
					signaturitData: signaturitData,
					currentFolder: currentFolder,
					loading: false,
					error: null,
				});
			})
			.catch((error) => {
				console.error("❌ Error fetching attachments:", error);

				// Si el error es 404, no es realmente un error - simplemente no hay documentos
				if (error.status === 404 || error.message?.includes("404")) {
					this.setState({
						attachments: [],
						signaturitData: [],
						loading: false,
						error: null, // No mostrar error para carpetas vacías
						currentFolder: this.state.folders.find((f) => f.id === folderId),
					});
				} else {
					this.setState({
						error: error.message || i18next.t("employeeDocuments.errorLoadingDocuments"),
						loading: false,
						attachments: [],
						signaturitData: [],
					});
				}
			});
	};

	sortAttachments = (attachments) => {
		const { sortField, sortDirection } = this.state;

		return [...attachments].sort((a, b) => {
			let aValue, bValue;

			switch (sortField) {
				case "name":
					aValue = a.name.toLowerCase();
					bValue = b.name.toLowerCase();
					break;
				case "created":
					aValue = new Date(a.created);
					bValue = new Date(b.created);
					break;
				case "size":
					aValue = a.size;
					bValue = b.size;
					break;
				default:
					aValue = a.name.toLowerCase();
					bValue = b.name.toLowerCase();
			}

			if (sortDirection === "asc") {
				return aValue > bValue ? 1 : -1;
			} else {
				return aValue < bValue ? 1 : -1;
			}
		});
	};

	closeModal() {
		this.props.hideModal();
	}

	openUploadModal() {
		this.props.showModal(
			{
				open: true,
				title: i18next.t("employeeDocuments.modalUploadTitle"),
				style: { width: "700px" },
				content: (
					<EmployeeDocumentsUploadModal
						handleClose={this.closeModal}
						folderId={this.state.selectedFolderId}
						employeeId={this.props.employee.id}
						onUploadSuccess={this.handleUploadSuccess}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	}

	handleUploadSuccess = () => {
		// Refrescar los attachments después de subir
		this.fetchAttachments(this.state.selectedFolderId);
		this.closeModal();
	};

	handleDownload = async (attachment) => {
		const { selectedFolderId } = this.state;
		const requestOptions = {
			method: "GET",
			headers: {
				Authorization: `Bearer ${authService.token.access_token}`,
			},
		};

		try {
			const response = await fetch(
				`${global.BASE_API}store-folders/${selectedFolderId}/attachments/${attachment.url}`,
				requestOptions
			);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const blob = await response.blob();

			// Crear un enlace temporal para descargar el archivo
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = attachment.name || "documento";
			document.body.appendChild(link);
			link.click();

			// Limpiar
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error("Error al descargar el archivo:", error);
			toastr.error(i18next.t("common.error"), i18next.t("employeeDocuments.errorDownload"));
		}
	};

	handleDelete = (attachment) => {
		const { name, url } = attachment;
		const { selectedFolderId } = this.state;

		this.props.showModal(
			{
				open: true,
				title: i18next.t("employeeDocuments.deleteTitle"),
				message: i18next.t("employeeDocuments.deleteConfirm", { name }),
				deleteAction: () => {
					this.props.hideModal();
					this.deleteAttachment(selectedFolderId, url, name);
				},
				style: { width: "400px" },
				closeModal: this.closeModal,
			},
			"delete"
		);
	};

	handleSign = (attachment) => {
		// Verificar si el documento es compatible para firma
		if (!isSignableDocument(attachment.name)) {
			toastr.error(
				"Formato no compatible",
				getFormatErrorMessage(attachment.name)
			);
			return;
		}

		// Verificar si tiene acceso a la funcionalidad de firmar documentos
		if (!hasSubscriptionFeature("SIGN_DOCUMENTS")) {
			this.setState({
				showUpgradeModal: true,
				upgradeFeature: i18next.t("employeeDocuments.signWithSignaturit"),
			});
			return;
		}

		const { selectedFolderId } = this.state;
		const { employee } = this.props;

		this.props.showModal(
			{
				open: true,
				title: i18next.t("employeeDocuments.signTitle"),
				style: { width: "800px" },
				content: (
					<DocumentSignContent
						closeModal={this.closeModal}
						attachment={attachment}
						storeFolderId={selectedFolderId}
						currentUser={this.props.user}
						currentEmployee={employee}
						onSignSuccess={this.handleSignSuccess}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	};

	handleSignSuccess = () => {
		// Recargar los attachments después de firmar
		this.fetchAttachments(this.state.selectedFolderId);
	};

	handleMove = (attachment) => {
		const { selectedFolderId } = this.state;

		this.props.showModal(
			{
				open: true,
				title: i18next.t("employeeDocuments.moveTitle"),
				style: { width: "500px" },
				content: (
					<DocumentMoveModal
						handleClose={this.closeModal}
						attachment={attachment}
						currentFolderId={selectedFolderId}
						onMoveSuccess={this.handleMoveSuccess}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	};

	handleMoveSuccess = () => {
		// Recargar los attachments después de mover
		this.fetchAttachments(this.state.selectedFolderId);
	};

	handleRename = (attachment) => {
		const { selectedFolderId } = this.state;

		this.props.showModal(
			{
				open: true,
				title: i18next.t("employeeDocuments.renameTitle"),
				style: { width: "500px" },
				content: (
					<DocumentRenameModal
						handleClose={this.closeModal}
						attachment={attachment}
						currentFolderId={selectedFolderId}
						onRenameSuccess={this.handleRenameSuccess}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	};

	handleRenameSuccess = () => {
		// Recargar los attachments después de renombrar
		this.fetchAttachments(this.state.selectedFolderId);
	};

	deleteAttachment = (folderId, url, name) => {
		documentsService
			.deleteAttachment(folderId, url)
			.then((response) => {
				if (!response?.ok) {
					const backendError =
						response?.errors?.[0]?.description ||
						response?.message ||
						i18next.t("employeeDocuments.errorDelete");
					throw new Error(backendError);
				}

				this.setState((prev) => ({
					attachments: prev.attachments.filter((a) => a.url !== url),
				}));
				toastr.info(i18next.t("common.success"), i18next.t("employeeDocuments.deleteSuccess", { name }));
			})
			.catch((error) => {
				toastr.error(i18next.t("common.error"), error?.message || i18next.t("employeeDocuments.errorDelete"));
			});
	};

	getFolderIcon = (iconName) => {
		const IconComponent = iconMap[iconName] || FiFolder;
		return <IconComponent />;
	};

	getSystemFolderName = (folder) => {
		if (!folder.system) return folder.name;
		const namesByIcon = {
			'payslips-icon': i18next.t('documents.systemFolders.payslips'),
			'contracts-icon': i18next.t('documents.systemFolders.contracts'),
			'absences-icon': i18next.t('documents.systemFolders.absences'),
			'personal-documents-icon': i18next.t('documents.systemFolders.personalDocuments'),
		};
		return namesByIcon[folder.icon] || folder.name;
	};

	renderFolderFilters = () => {
		const { folders, selectedFolderId, loading } = this.state;

		if (loading) {
			return <Loading />;
		}

		// Orden específico para carpetas del sistema
		const systemFolderOrder = {
			nóminas: 1,
			contratos: 2,
			ausencias: 3,
			"documentos personales": 4,
		};

		const sortFolders = (folders) => {
			return folders.sort((a, b) => {
				// Primero las carpetas del sistema en orden específico
				if (a.system && b.system) {
					const aOrder = systemFolderOrder[a.name.toLowerCase()] || 999;
					const bOrder = systemFolderOrder[b.name.toLowerCase()] || 999;
					return aOrder - bOrder;
				}

				// Luego las carpetas del sistema (que no están en el orden específico)
				if (a.system && !b.system) return -1;
				if (!a.system && b.system) return 1;

				// Finalmente las carpetas de usuario alfabéticamente
				return a.name.localeCompare(b.name);
			});
		};

		const sortedFolders = sortFolders([...folders]);

		return (
			<div className="mb-3">
				<div
					className="btn-group"
					role="group"
					style={{ flexWrap: "wrap", padding: "0 1px" }}
				>
					{sortedFolders.map((folder) => (
						<button
							key={folder.id}
							type="button"
							className={`btn ${selectedFolderId === folder.id ? "btn-primary" : "btn-outline-primary"}`}
							onClick={() => this.selectFolder(folder.id)}
							style={{
								display: "flex",
								alignItems: "center",
								gap: "5px",
								fontSize: "14px",
								padding: "6px 12px",
							}}
						>
							<span style={{ fontSize: "14px" }}>
								{this.getFolderIcon(folder.icon)}
							</span>
							{this.getSystemFolderName(folder)}
						</button>
					))}
					{/* Botón de menú contextual al final */}
					<Dropdown>
						<Dropdown.Toggle as={CustomEllipsisButton} />
						<Dropdown.Menu>
							<Dropdown.Item as="button" onClick={this.openCopyFoldersModal}>
								{i18next.t("employeeDocuments.copyFolders")}
							</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				</div>
			</div>
		);
	};

	openCopyFoldersModal = () => {
		const { employee } = this.props;
		this.props.showModal(
			{
				open: true,
				title: i18next.t("employeeDocuments.copyFolders"),
				style: { width: "600px" },
				content: (
					<CopyStoreFoldersModal
						closeModal={this.closeModal}
						sourceEmployeeId={employee?.id}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	};

	// Método auxiliar para hacer matching entre attachments y signaturit data
	getSignatureDataForAttachment = (attachment) => {
		const { signaturitData } = this.state;

		if (!signaturitData || signaturitData.length === 0) {
			return null;
		}

		// Extraer el UUID del attachment.url
		const attachmentUuid = attachment.url;

		// Buscar TODOS los matches por original_file_uuid o completed_file_uuid
		// (un documento puede tener múltiples procesos de firma)
		const matches = signaturitData.filter(
			(sig) =>
				sig.original_file_uuid === attachmentUuid ||
				sig.completed_file_uuid === attachmentUuid
		);

		if (matches.length === 0) {
			return null;
		}

		// Si hay múltiples matches, retornar el más reciente por fecha de creación
		if (matches.length > 1) {
			matches.sort((a, b) => {
				const dateA = new Date(a.created);
				const dateB = new Date(b.created);
				return dateB - dateA; // Orden descendente (más reciente primero)
			});
		}

		return matches[0];
	};

	getDocumentColumns = () => [
		{
			key: "name",
			header: i18next.t("employeeDocuments.colDocument"),
			cellStyle: { width: "40%" },
			render: (attachment) => (
				<button className="btn-transparent btn-editable" onClick={() => this.handleDownload(attachment)}>
					{attachment.name}
				</button>
			),
		},
		{
			key: "size",
			header: i18next.t("employeeDocuments.colSize"),
			sortAccessor: (attachment) => attachment.size || 0,
			render: (attachment) => {
				const inMegabytes = attachment.size / 1000000;
				const size = inMegabytes < 1 ? attachment.size / 1000 : inMegabytes;
				return `${size.toFixed(2)}${inMegabytes < 1 ? "Kb" : "Mb"}`;
			},
		},
		{
			key: "created",
			header: i18next.t("employeeDocuments.colCreated"),
			sortAccessor: (attachment) => new Date(attachment.created).getTime(),
			render: (attachment) => new Date(attachment.created)
				.toLocaleString("es-ES", {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
					hour: "2-digit",
					minute: "2-digit",
				})
				.replace(",", " "),
		},
		{
			key: "creator",
			header: i18next.t("employeeDocuments.colAuthor"),
			sortAccessor: (attachment) => attachment.creator
				? `${attachment.creator.name} ${attachment.creator.surname}`
				: "",
			render: (attachment) => attachment.creator
				? `${attachment.creator.name} ${attachment.creator.surname}`
				: "",
		},
		{
			key: "status",
			header: i18next.t("employeeDocuments.colStatus"),
			sortable: false,
			render: (attachment) => {
				const signatureData = this.getSignatureDataForAttachment(attachment);
				const isAdmin = this.props.user?.roles?.includes("ADMIN");
				return (
					<SignatureStatus
						signatureData={signatureData}
						onStartSign={() => this.handleSign(attachment)}
						canSign={isSignableDocument(attachment.name) && isAdmin}
						hasSubscriptionAccess={hasSubscriptionFeature("SIGN_DOCUMENTS")}
					/>
				);
			},
		},
	];

	renderDocumentActions = (attachment) => {
		const isAdmin = this.props.user?.roles?.includes("ADMIN");
		const hasSignDocumentsSubscription = hasSubscriptionFeature("SIGN_DOCUMENTS");
		const isSignable = isSignableDocument(attachment.name);
		const canSign = hasSignDocumentsSubscription && isSignable && isAdmin;

		return (
			<Dropdown>
				<Dropdown.Toggle variant="action"><FiMoreHorizontal /></Dropdown.Toggle>
				<Dropdown.Menu>
					<Dropdown.Item
						as="button"
						onClick={() => this.handleSign(attachment)}
						disabled={!canSign}
						className={!canSign ? "disabled" : ""}
						title={!isSignable
							? getFormatErrorMessage(attachment.name)
							: !hasSignDocumentsSubscription
								? i18next.t("employeeDocuments.signTooltipPlan")
								: !isAdmin ? i18next.t("employeeDocuments.signTooltipAdmin") : ""}
						style={{ opacity: canSign ? 1 : 0.5, cursor: canSign ? "pointer" : "not-allowed" }}
					>
						{isHaddockEnvironment()
							? i18next.t("employeeDocuments.signDocument")
							: i18next.t("employeeDocuments.signWithSignaturit")}
					</Dropdown.Item>
					<Dropdown.Item as="button" onClick={() => this.handleRename(attachment)}>{i18next.t("employeeDocuments.menuRename")}</Dropdown.Item>
					<Dropdown.Item as="button" onClick={() => this.handleDownload(attachment)}>{i18next.t("employeeDocuments.menuDownload")}</Dropdown.Item>
					<Dropdown.Item as="button" onClick={() => this.handleMove(attachment)}>{i18next.t("employeeDocuments.menuMove")}</Dropdown.Item>
					<Dropdown.Item as="button" onClick={() => this.handleDelete(attachment)}>{i18next.t("common.delete")}</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	render() {
		const { attachments, loading, error, folders, dataLoaded } = this.state;

		// Si no está visible y no se han cargado los datos, mostrar estado de carga
		if (this.props.isVisible === false && !dataLoaded) {
			return <Loading />;
		}

		if (loading && !attachments.length) {
			return <Loading />;
		}

		// Manejar errores de manera amigable
		if (error) {
			return (
				<div className="alert alert-warning">
					<strong>{i18next.t("employeeDocuments.infoLabel")}:</strong> {error}
					<br />
					<small>
						{i18next.t("employeeDocuments.errorRefresh")}
					</small>
				</div>
			);
		}

		// Si no hay carpetas, mostrar mensaje informativo
		if (!folders || folders.length === 0) {
			return (
				<div className="alert alert-info">
					<strong>{i18next.t("employeeDocuments.infoLabel")}:</strong> {i18next.t("employeeDocuments.noFoldersInfo")}
					<br />
					<small>
						{i18next.t("employeeDocuments.noFoldersContact")}
					</small>
				</div>
			);
		}

		return (
			<>
				<div>
					{this.renderFolderFilters()}
					<div className="employee-profile-datatable-wrap">
						<DataTable
							tableClassName="table-groups table-employee-documents table-hover"
							columns={this.getDocumentColumns()}
							rows={attachments || []}
							rowKey={(attachment) => attachment.url}
							emptyMessage={i18next.t("employeeDocuments.emptyFolder")}
							actionsColumn={{
								render: this.renderDocumentActions,
								className: "td-actions",
								header: <button type="button" title={i18next.t("employeeDocuments.btnUpload")} onClick={this.openUploadModal} className="btn btn-new"><FiPlus /></button>,
							}}
						/>
					</div>
				</div>
				<UpgradeModal
					show={this.state.showUpgradeModal}
					onClose={() => this.setState({ showUpgradeModal: false })}
					feature={this.state.upgradeFeature}
					requiredPlan="Pro"
				/>
			</>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		user: state.authReducer.user,
		employee: state.employeesReducer.employee,
	};
};

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeDocuments);
